import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  ReactNode,
} from 'react';
import type {Plan} from '../components/plan-card/PlanCard';
import type {BillingCycle} from '../constants/plans';
import {BILLING_CYCLE} from '../constants/plans';
import {FREE_PLAN_NAME, FREE_PLAN_NAMES, PRISMM} from '../../constants/plan';
import {useFetchData, useSubmitData} from '../hooks';

// --- Types ---

type TeamInfo = {
  subscription_end_date: string | null;
  plan_start_date: string | null;
  next_billing_date: string | null;
  renewal_date: string | null;
};

type UserInfo = {
  email: string | null;
};

export type PlanTier = 'free' | 'paid' | 'enterprise';

export type CurrentPlan = {
  billing_cycle: BillingCycle;
  client_type: string;
  client_sub_type: string;
  display_name: string;
  features: Record<string, string | number | boolean | null>;
  mapped_client_type: 'planner' | 'supplier';
  plan_name: string;
  tier: PlanTier;
};

// TODO: Define once GET /teams/:teamId/plan/scheduled-changes endpoint exists
export type ScheduledPlanChange = {
  stripe_schedule_id: string;
  target_plan: string;
  target_billing_cycle: BillingCycle;
  start_date: string;
};

// TODO: Define once GET endpoint for scheduled cancellation exists
type ScheduledCancellation = {
  cancel_at: string;
};

type UserPlanInfoResponse = {
  available_plans: Array<Plan>;
  billing_cycle: string;
  client_type: string;
  client_sub_type: string | null;
  feature_limits: Record<string, string | number | boolean | null>;
  mapped_client_type: 'planner' | 'supplier';
  next_billing_date: string | null;
  plan_display_name: string;
  plan_start_date: string | null;
  plan_name: string;
  renewal_date: string | null;
  scheduled_plan_change: ScheduledPlanChange | null;
  subscription_end_date: string | null;
  tier: PlanTier;
  user_email: string | null;
};

type PlanChangeState = {
  team: TeamInfo;
  user: UserInfo;
  currentPlan: CurrentPlan;
  availablePlans: Plan[];
  selectedPlan: Plan | null;
  scheduledPlanChange: ScheduledPlanChange | null;
  scheduledCancellation: ScheduledCancellation | null;
  billingCycle: BillingCycle;
  businessType: string;
  businessSubType: string | null;
  isFreePlan: boolean;
  isPrismmFreePlan: boolean;
  isLoading: boolean;
  isError: boolean;
  isPlanExpired: boolean;
};

type PlanChangeActions = {
  selectPlan: (plan: Plan | null) => void;
  setBillingCycle: (cycle: BillingCycle) => void;
  confirmUpgrade: (
    address?: Record<string, string | undefined>
  ) => Promise<unknown> | undefined;
  confirmDowngrade: () => Promise<unknown> | undefined;
  revokeDowngrade: () => Promise<unknown> | undefined;
  revokeCancellation: () => Promise<unknown> | undefined;
  refetch: () => void;
};

type PlanChangeContextType = PlanChangeState & PlanChangeActions;

// --- Context ---

const PlanChangeContext = createContext<PlanChangeContextType | undefined>(
  undefined
);

// --- Provider ---

export const PlanChangeProvider = ({children}: {children: ReactNode}) => {
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [billingCycleOverride, setBillingCycleOverride] =
    useState<BillingCycle | null>(null);

  const {
    data: planData,
    isError,
    isLoading,
    refetchQuery,
  } = useFetchData<UserPlanInfoResponse>({
    url: '/user-plan-information',
    fileName: 'PlanChangeContext',
  });

  const {submit: submitUpgrade} = useSubmitData<unknown>({
    fileName: 'PlanChangeContext:upgrade',
  });
  const {submit: submitDowngrade} = useSubmitData<unknown>({
    fileName: 'PlanChangeContext:downgrade',
  });
  const {submit: submitRevokeDowngrade} = useSubmitData<unknown>({
    fileName: 'PlanChangeContext:revokeDowngrade',
  });
  const {submit: submitRevokeCancellation} = useSubmitData<unknown>({
    fileName: 'PlanChangeContext:revokeCancellation',
  });

  // Derived state
  const currentPlan: CurrentPlan = useMemo(() => {
    function resolveBillingCycle(): BillingCycle {
      if (planData?.billing_cycle) {
        return planData.billing_cycle as BillingCycle;
      }
      if (planData?.plan_name?.includes(PRISMM)) {
        return 'monthly';
      }
      return BILLING_CYCLE[0];
    }

    return {
      plan_name: planData?.plan_name ?? '',
      display_name: planData?.plan_display_name ?? '',
      billing_cycle: resolveBillingCycle(),
      client_type: planData?.client_type ?? '',
      client_sub_type: planData?.client_sub_type ?? '',
      mapped_client_type: planData?.mapped_client_type ?? 'planner',
      features: planData?.feature_limits ?? {},
      tier: planData?.tier ?? 'paid',
    };
  }, [planData]);

  const isFreePlan = FREE_PLAN_NAMES.includes(currentPlan.plan_name);
  const isPrismmFreePlan =
    currentPlan.plan_name.includes(PRISMM) &&
    currentPlan.plan_name.includes(FREE_PLAN_NAME);
  const isPlanExpired =
    !!planData?.subscription_end_date &&
    new Date(planData.subscription_end_date) < new Date();
  const canToggleBillingCycle = isFreePlan || isPrismmFreePlan || isPlanExpired;

  const billingCycle: BillingCycle = useMemo(() => {
    // Free users can toggle billing cycle; paid users are locked
    if (canToggleBillingCycle && billingCycleOverride) {
      return billingCycleOverride;
    }
    return currentPlan.billing_cycle;
  }, [canToggleBillingCycle, billingCycleOverride, currentPlan.billing_cycle]);

  const team: TeamInfo = useMemo(
    () => ({
      subscription_end_date: planData?.subscription_end_date ?? null,
      plan_start_date: planData?.plan_start_date ?? null,
      next_billing_date: planData?.next_billing_date ?? null,
      renewal_date: planData?.renewal_date ?? null,
    }),
    [planData]
  );

  const user: UserInfo = useMemo(
    () => ({
      email: planData?.user_email ?? null,
    }),
    [planData]
  );

  // Actions
  const selectPlan = useCallback((plan: Plan | null) => {
    setSelectedPlan(plan);
  }, []);

  const setBillingCycle = useCallback(
    (cycle: BillingCycle) => {
      if (canToggleBillingCycle) {
        setBillingCycleOverride(cycle);
      }
    },
    [canToggleBillingCycle]
  );

  const confirmUpgrade = useCallback(
    (address?: Record<string, string | undefined>) => {
      const body: Record<string, unknown> = {
        plan_name: selectedPlan?.plan_type,
        billing_cycle: billingCycle,
        email: user.email,
        _csrf: window.csrfToken ?? '',
      };
      if (address) {
        body.address = address;
      }
      return submitUpgrade('/teams/plan/upgrade-confirm', 'POST', body);
    },
    [selectedPlan, billingCycle, user.email, submitUpgrade]
  );

  const confirmDowngrade = useCallback(() => {
    return submitDowngrade('/teams/plan/downgrade-schedule', 'POST', {
      plan_name: selectedPlan?.plan_type,
      billing_cycle: billingCycle,
      _csrf: window.csrfToken ?? '',
    });
  }, [selectedPlan, billingCycle, submitDowngrade]);

  const revokeDowngrade = useCallback(() => {
    return submitRevokeDowngrade('/teams/plan/downgrade-schedule', 'DELETE');
  }, [submitRevokeDowngrade]);

  const revokeCancellation = useCallback(() => {
    return submitRevokeCancellation('/teams/plan/reactivate', 'POST');
  }, [submitRevokeCancellation]);

  const refetch = useCallback(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    refetchQuery();
  }, [refetchQuery]);

  const value: PlanChangeContextType = useMemo(
    () => ({
      // State
      team,
      user,
      currentPlan,
      availablePlans: planData?.available_plans ?? [],
      selectedPlan,
      scheduledPlanChange: planData?.scheduled_plan_change ?? null,
      scheduledCancellation: null, // TODO: populate once GET endpoint exists
      billingCycle,
      businessType: currentPlan.client_type,
      businessSubType: currentPlan.client_sub_type,
      isFreePlan,
      isPrismmFreePlan,
      isLoading,
      isPlanExpired,
      isError,
      // Actions
      selectPlan,
      setBillingCycle,
      confirmUpgrade,
      confirmDowngrade,
      revokeDowngrade,
      revokeCancellation,
      refetch,
    }),
    [
      team,
      user,
      currentPlan,
      planData?.available_plans,
      selectedPlan,
      billingCycle,
      isFreePlan,
      isPrismmFreePlan,
      isLoading,
      isError,
      isPlanExpired,
      selectPlan,
      setBillingCycle,
      confirmUpgrade,
      confirmDowngrade,
      revokeDowngrade,
      revokeCancellation,
      refetch,
    ]
  );

  return (
    <PlanChangeContext.Provider value={value}>
      {children}
    </PlanChangeContext.Provider>
  );
};

// --- Hook ---

export const usePlanChange = () => {
  const context = useContext(PlanChangeContext);
  if (context === undefined) {
    throw new Error('usePlanChange must be used within a PlanChangeProvider');
  }
  return context;
};
